import { Link, useNavigate } from 'react-router-dom';
import PageLayout from '@/components/shared/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { GoogleIcon, HarryAvatar } from '@/components/icons';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { auth, getAuthMethod, shouldUseRedirectAuth } from '@/lib/firebase-config';
import { setRedirectIntent } from '@/lib/auth-redirect-handler';
import {
  EMULATOR_GOOGLE_TEST_USERS,
  isEmulatorQuickSignInAllowed,
  signInWithEmulatorGoogleMock,
  type EmulatorGoogleUserKey,
} from '@/lib/emulator-google-sign-in';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { requestSelfServeSendSignInEmail } from '@/lib/sign-in-link-api';
import {
  isStaleSignInLinkError,
  loginHrefAfterSignInLink,
  replaceSignInLinkActionInHistory,
} from '@/lib/sign-in-link-callback';
import {
  LOGIN_NOTICE_COPY,
  clearLoginNotice,
  loginNoticeFromFirebaseLinkError,
  readLoginNotice,
  writeLoginNotice,
  type LoginNoticeKind,
} from '@/lib/login-user-messages';
import {
  SELF_SERVE_GENERIC_FAILURE_MESSAGE,
  SELF_SERVE_SUCCESS_MESSAGE,
} from '@/lib/sign-in-link-self-serve-public';
import { startCapture, setUserInfo, hasError, getErrorType, downloadLogs, stopCaptureAndSubmitIfError, captureAuthAttemptContext, clearAuthAttemptContext, getAuthAttemptCount } from '@/lib/auth-debug';
import {
  getLoginReturnTo,
  getPendingPurchasePriceId,
  isPurchaseLoginFlow,
  rememberPendingPurchasePriceId,
} from '@/lib/purchase-handoff-client';
import { resolveLoginDestination, userHasAppAccess } from '@/lib/access-state';

import logger from '@/lib/logging';

function getEmailHintFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const email = new URLSearchParams(window.location.search).get('email')?.trim().toLowerCase();
  return email && email.includes('@') ? email : null;
}

function scrollEmailInputIntoView(event: FocusEvent<HTMLInputElement>) {
  window.requestAnimationFrame(() => {
    event.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
}

export default function LoginPage() {
  const navigate = useNavigate();
  const returnTo = getLoginReturnTo();
  const isPurchaseFlow = isPurchaseLoginFlow();
  const { toast } = useToast();
  const { isAuthenticated, loading, serverUser } = useAuth();
  const [needsEmailForLink, setNeedsEmailForLink] = useState(false);
  const [linkCompletionEmail, setLinkCompletionEmail] = useState('');
  const [completingLink, setCompletingLink] = useState(false);
  const [autoCompletingLink, setAutoCompletingLink] = useState(false);
  const [requestLinkEmail, setRequestLinkEmail] = useState(() => getEmailHintFromUrl() ?? '');
  const [requestingLink, setRequestingLink] = useState(false);
  const [emulatorSigningInAs, setEmulatorSigningInAs] = useState<EmulatorGoogleUserKey | null>(null);
  const [showRedirectFallback, setShowRedirectFallback] = useState(false);
  const [loginNotice, setLoginNotice] = useState<LoginNoticeKind | null>(() => readLoginNotice());
  const emailLinkAttemptRef = useRef(0);

  const showEmulatorQuickSignIn = isEmulatorQuickSignInAllowed(auth);

  // Stash the priceId from a /purchase?priceId=… returnTo so whichever flow
  // mints the session (Google popup/redirect or email link) can send it to
  // /api/auth/login for server-side revalidation (ADR 0003).
  useEffect(() => {
    if (isPurchaseFlow) {
      rememberPendingPurchasePriceId(returnTo);
    }
  }, [isPurchaseFlow, returnTo]);

  // Start diagnostic logger on login page mount
  useEffect(() => {
    const initDebugCapture = async () => {
      logger.debug('🔍 [LoginPage] Checking diagnostic logging...');
      const started = await startCapture('login_page_loaded');
      if (started) {
        logger.debug('🔍 [LoginPage] Diagnostic capture started');
      } else {
        logger.debug('🔍 [LoginPage] Diagnostic logging is disabled (enable in admin settings)');
      }
    };
    initDebugCapture();
  }, []);

  const completeEmailLinkSignIn = async (email: string, attempt: number) => {
    if (!auth) return;
    const normalized = email.trim().toLowerCase();
    if (!normalized.includes('@')) {
      toast({
        variant: 'destructive',
        title: 'Sign-in Error',
        description: 'Enter the email address your administrator invited.',
      });
      return;
    }

    setCompletingLink(true);
    setUserInfo(normalized, null);
    const hrefAtStart = window.location.href;

    try {
      logger.debug('🔗 [LoginPage] Completing sign-in link for:', normalized);
      const result = await signInWithEmailLink(auth, normalized, hrefAtStart);
      if (attempt !== emailLinkAttemptRef.current) return;

      if (result.user) {
        window.localStorage.removeItem('emailForSignIn');
        setNeedsEmailForLink(false);

        window.history.replaceState({}, document.title, loginHrefAfterSignInLink(hrefAtStart));
        clearLoginNotice();
        setLoginNotice(null);
        toast({
          variant: 'default',
          title: 'Sign-in Successful',
          description: 'Welcome back!',
        });
      }
    } catch (error: unknown) {
      if (attempt !== emailLinkAttemptRef.current) return;
      const err = error as { code?: string; message?: string };
      replaceSignInLinkActionInHistory();
      if (isStaleSignInLinkError(err, auth.currentUser)) {
        logger.debug('[LoginPage] Ignoring stale sign-in link after another sign-in succeeded');
        return;
      }
      logger.error('❌ [LoginPage] Sign-in link error:', error);
      stopCaptureAndSubmitIfError();
      const notice = loginNoticeFromFirebaseLinkError(err.code);
      writeLoginNotice(notice);
      setLoginNotice(notice);
      setRequestLinkEmail((current) => current || normalized);
      const copy = LOGIN_NOTICE_COPY[notice];
      toast({
        variant: 'destructive',
        title: copy.title,
        description: copy.description,
      });
    } finally {
      if (attempt === emailLinkAttemptRef.current) {
        setCompletingLink(false);
        setAutoCompletingLink(false);
      }
    }
  };

  // Sign-in link callback: auto-complete when email is stored or embedded in continue URL
  useEffect(() => {
    if (!auth) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    if (auth.currentUser) {
      replaceSignInLinkActionInHistory();
      return;
    }

    logger.debug('🔗 [LoginPage] Sign-in link detected in URL');
    const storedEmail = window.localStorage.getItem('emailForSignIn');
    const urlEmail = getEmailHintFromUrl();
    const email = (storedEmail || urlEmail)?.trim().toLowerCase();

    const attempt = ++emailLinkAttemptRef.current;
    if (email && email.includes('@')) {
      setAutoCompletingLink(true);
      void completeEmailLinkSignIn(email, attempt);
      return;
    }

    setNeedsEmailForLink(true);
    if (urlEmail) {
      setLinkCompletionEmail(urlEmail);
    }
  }, []);

  // Redirect only after server session is established (roles come from session cookie).
  // Firebase-only auth caused a race: dashboard loaded before /api/auth/login finished.
  useEffect(() => {
    logger.debug('[LoginPage] Auth state:', { isAuthenticated, loading, returnTo, hasServerUser: !!serverUser });
    if (loading || !isAuthenticated) {
      if (!loading && !isAuthenticated) {
        logger.debug('ℹ️ [LoginPage] Not authenticated, showing login form');
      }
      return;
    }

    if (!serverUser) {
      logger.debug('⏳ [LoginPage] Firebase user present; waiting for server session before redirect');
      return;
    }

    const roles = serverUser.roles ?? [];
    const destination = resolveLoginDestination({
      hasAppAccess: userHasAppAccess(roles),
      roles,
      returnTo,
    });

    logger.debug('✅ [LoginPage] Server session ready, redirecting to', destination, { roles });
    clearLoginNotice();
    navigate(destination, { replace: true });
  }, [isAuthenticated, loading, serverUser, navigate, returnTo]);

  const handleRequestSignInLink = async () => {
    const normalized = requestLinkEmail.trim().toLowerCase();
    if (!normalized.includes('@')) {
      toast({
        variant: 'destructive',
        title: 'Email required',
        description: 'Enter the email address you were invited with.',
      });
      return;
    }

    setRequestingLink(true);
    try {
      const result = await requestSelfServeSendSignInEmail(normalized, {
        priceId: isPurchaseFlow ? getPendingPurchasePriceId() : undefined,
        returnTo,
      });
      if (!result.success) {
        toast({
          variant: 'destructive',
          title: 'Sign-in email',
          description: result.message || SELF_SERVE_GENERIC_FAILURE_MESSAGE,
        });
        return;
      }
      toast({
        title: 'Check your email',
        description: SELF_SERVE_SUCCESS_MESSAGE,
      });
      setRequestLinkEmail('');
    } finally {
      setRequestingLink(false);
    }
  };

  async function signInAsEmulatorUser(userKey: EmulatorGoogleUserKey) {
    if (!isEmulatorQuickSignInAllowed(auth)) return;

    setEmulatorSigningInAs(userKey);
    try {
      const result = await signInWithEmulatorGoogleMock(auth, userKey);
    } catch (error: any) {
      logger.error('❌ [LoginPage] Emulator quick sign-in failed:', error);
      toast({
        variant: 'destructive',
        title: 'Emulator sign-in failed',
        description: error.message || 'Could not sign in with the emulator test user.',
      });
    } finally {
      setEmulatorSigningInAs(null);
    }
  }

  async function signInWithGoogle(forceRedirect = false) {
    // Track timing for the entire OAuth flow
    const oauthStartTime = performance.now();

    try {
      // CRITICAL: Capture context BEFORE attempting authentication
      // This ensures we have diagnostic info even if auth fails
      const useRedirectAuth = forceRedirect || shouldUseRedirectAuth();
      const authContext = captureAuthAttemptContext(useRedirectAuth ? 'redirect' : 'popup');
      const attemptCount = getAuthAttemptCount();

      // Detect mobile device for diagnostic purposes
      const ua = navigator.userAgent;
      const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);
      const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line|WeChat|Snapchat/i.test(ua);

      logger.debug('═══════════════════════════════════════════════════════════');
      logger.debug(`🚀 [LoginPage] GOOGLE SIGN-IN ATTEMPT #${attemptCount} STARTED`);
      logger.debug('═══════════════════════════════════════════════════════════');

      // Log environment context (full User-Agent, no truncation)
      logger.debug('📍 [LoginPage] ENVIRONMENT CONTEXT:');
      logger.debug(`   - Current URL: ${window.location.href}`);
      logger.debug(`   - Hostname: ${window.location.hostname}`);
      logger.debug(`   - Protocol: ${window.location.protocol}`);
      logger.debug(`   - Referrer: ${document.referrer || 'direct'}`);
      logger.debug(`   - User Agent (FULL): ${ua}`);
      logger.debug(`   - Timestamp: ${new Date().toISOString()}`);
      logger.debug(`   - Attempt Number: ${attemptCount}`);
      logger.debug(`   - Cookies Enabled: ${navigator.cookieEnabled ? 'YES' : '❌ NO - WILL FAIL'}`);

      // Mobile-specific diagnostics
      logger.debug('📱 [LoginPage] DEVICE DIAGNOSTICS:');
      logger.debug(`   - Is Mobile: ${isMobile ? '📱 YES' : 'NO (desktop)'}`);
      logger.debug(`   - Is iOS: ${isIOS}`);
      logger.debug(`   - Is Safari: ${isSafari}`);
      logger.debug(`   - Is In-App Browser: ${isInAppBrowser ? '⚠️ YES (popups may be blocked!)' : 'NO'}`);
      logger.debug(`   - Screen: ${window.screen.width}x${window.screen.height}`);
      logger.debug(`   - Viewport: ${window.innerWidth}x${window.innerHeight}`);
      logger.debug(`   - Device Pixel Ratio: ${window.devicePixelRatio}`);
      logger.debug(`   - Online: ${navigator.onLine}`);
      logger.debug(`   - Language: ${navigator.language}`);

      if (isMobile) {
        if (useRedirectAuth) {
          logger.debug('📱 [LoginPage] Mobile local dev — using redirect auth (popup relay is unreliable here)');
        } else {
          logger.warn('⚠️ [LoginPage] Mobile device detected - popup auth may be unreliable');
          logger.warn('   Consider using redirect auth flow for better mobile compatibility');
        }
      }
      if (isInAppBrowser) {
        logger.error('🚨 [LoginPage] IN-APP BROWSER DETECTED - OAuth popup WILL LIKELY FAIL');
        logger.error('   User should open this page in their default browser');
      }

      // Early warning if cookies are disabled
      if (!navigator.cookieEnabled) {
        logger.error('❌ [LoginPage] CRITICAL: Cookies are disabled!');
        logger.error('   Authentication will fail without cookies enabled.');
        toast({
          variant: 'destructive',
          title: 'Cookies Required',
          description: 'Please enable cookies in your browser to sign in.',
        });
        return;
      }

      // Get the configured auth method
      const authMethod = getAuthMethod();

      // Log Firebase configuration
      logger.debug('🔥 [LoginPage] FIREBASE CONFIGURATION:');
      logger.debug(`   - Auth Method: ${authMethod}`);
      logger.debug(`   - Use Redirect Auth: ${useRedirectAuth}`);
      logger.debug(`   - Auth Domain: ${(auth as any)?.config?.authDomain || 'unknown'}`);
      logger.debug(`   - Project ID: ${(auth as any)?.app?.options?.projectId || 'unknown'}`);
      logger.debug(`   - Auth Instance Ready: ${!!auth}`);
      logger.debug(`   - Current User Before Attempt: ${auth?.currentUser?.email || 'none'}`);

      const provider = new GoogleAuthProvider();

      // Log provider setup
      logger.debug('👤 [LoginPage] OAUTH PROVIDER SETUP:');
      logger.debug(`   - Provider ID: ${provider.providerId}`);
      logger.debug(`   - Custom Params: prompt=select_account`);

      // Add prompt to force account selection
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      if (!auth) return;

      emailLinkAttemptRef.current += 1;
      setAutoCompletingLink(false);
      setCompletingLink(false);
      replaceSignInLinkActionInHistory();

      // Redirect on mobile, in-app browsers, Auth emulator, and stable hosted domains.
      // Preview channels use popup (see shouldUseRedirectAuth).
      if (useRedirectAuth) {
        logger.debug('↪️ [LoginPage] INITIATING REDIRECT AUTH FLOW...');
        setRedirectIntent();
        await signInWithRedirect(auth, provider);
        return;
      }

      const popupStartTime = performance.now();
      logger.debug('🖼️ [LoginPage] INITIATING POPUP AUTH FLOW...');
      logger.debug('   - Waiting for user to select Google account in popup...');
      logger.debug('   - If popup is blocked, auth will fail with auth/popup-blocked');

      const result = await signInWithPopup(auth, provider);
      const popupDuration = Math.round(performance.now() - popupStartTime);

      if (result.user) {
        logger.debug(`✅ [LoginPage] Google sign-in successful via popup! (took ${popupDuration}ms)`);
        logger.debug('[LoginPage] User Details:', {
          email: result.user.email,
          uid: result.user.uid,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          emailVerified: result.user.emailVerified,
          isAnonymous: result.user.isAnonymous,
          metadata: result.user.metadata,
        });
        logger.debug('[LoginPage] Credential:', (result as any).credential);
        logger.debug('[LoginPage] Provider account info:', (result as any).additionalUserInfo);

        // Clear auth context on success
        clearAuthAttemptContext();

        // AuthContext completeLogin handles profile upsert and session cookie
      }
    } catch (error: any) {
      const oauthDuration = Math.round(performance.now() - oauthStartTime);
      const attemptCount = getAuthAttemptCount();
      const mobileUa = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Popup relay often fails on mobile even when the user selected an account.
      if (error?.code === 'auth/popup-closed-by-user' && mobileUa && auth) {
        try {
          logger.warn('⚠️ [LoginPage] Popup relay failed on mobile — retrying with redirect auth');
          const fallbackProvider = new GoogleAuthProvider();
          fallbackProvider.setCustomParameters({ prompt: 'select_account' });
          setRedirectIntent();
          await signInWithRedirect(auth, fallbackProvider);
          return;
        } catch (redirectError) {
          logger.error('❌ [LoginPage] Redirect fallback after popup failure also failed:', redirectError);
          error = redirectError;
        }
      }

      // ═══════════════════════════════════════════════════════════
      // CRITICAL: All error details MUST be logged synchronously here
      // before the AuthDebugMonitor (with 500ms delay) stops recording.
      // Do NOT use await or async calls before logging error details.
      // ═══════════════════════════════════════════════════════════

      logger.debug('═══════════════════════════════════════════════════════════');
      logger.error(`❌ [LoginPage] GOOGLE SIGN-IN FAILED (Attempt #${attemptCount}, Duration: ${oauthDuration}ms)`);
      logger.debug('═══════════════════════════════════════════════════════════');

      // Log comprehensive error information
      logger.error('🔴 [LoginPage] ERROR DETAILS:');
      logger.error(`   - Error Code: ${error.code || 'NO CODE - check error object below'}`);
      logger.error(`   - Error Message: ${error.message || 'NO MESSAGE - check error object below'}`);
      logger.error(`   - Error Name: ${error.name || 'unknown'}`);
      logger.error(`   - Error Type: ${error.constructor?.name || typeof error}`);
      logger.error(`   - Duration: ${oauthDuration}ms`);
      logger.error(`   - Attempt Number: ${attemptCount}`);
      logger.error(`   - Timestamp: ${new Date().toISOString()}`);
      logger.error(`   - Cookies Enabled: ${navigator.cookieEnabled}`);
      logger.error(`   - Online Status: ${navigator.onLine}`);

      // Duration-based diagnostics
      if (oauthDuration < 1000) {
        logger.error('   ⚡ VERY FAST FAILURE (<1s) - likely popup blocked or config error');
      } else if (oauthDuration < 5000) {
        logger.error('   ⏱️ QUICK FAILURE (<5s) - likely user closed popup or in-app browser issue');
      } else if (oauthDuration > 30000) {
        logger.error('   🐌 SLOW FAILURE (>30s) - likely network timeout or server issue');
      }

      // Log additional Firebase error properties if available
      if (error.customData) {
        logger.error('   - Custom Data:', error.customData);
      }
      if (error.email) {
        logger.error(`   - Associated Email: ${error.email}`);
      }
      if (error.credential) {
        logger.error('   - Credential Info:', error.credential);
      }
      if (error.serverResponse) {
        logger.error('   - Server Response:', error.serverResponse);
      }

      // Log the full error object (now properly serialized, not empty {})
      logger.error('📋 [LoginPage] FULL ERROR:', error);
      if (error.stack) {
        logger.error('📋 [LoginPage] ERROR STACK:', error.stack);
      }

      // Trigger automatic debug log submission on error
      // NOTE: This is also triggered by AuthDebugMonitor after 500ms delay,
      // but we call it explicitly here to ensure it runs even if the monitor
      // was not active. The function is idempotent (checks isRecording).
      stopCaptureAndSubmitIfError().then(result => {
        if (result.submitted && 'issueUrl' in result) {
          logger.debug('[LoginPage] Auth debug issue created automatically:', (result as { issueUrl?: string }).issueUrl ?? '');
        }
      });

      // Handle specific error cases with user-friendly messages
      if (
        error.code === 'auth/popup-blocked' ||
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        setShowRedirectFallback(true);
      }

      if (error.code === 'auth/unauthorized-domain') {
        const currentDomain = window.location.hostname;
        toast({
          variant: 'destructive',
          title: 'Sign-in Failed: Unauthorized Domain',
          description: `The domain "${currentDomain}" is not authorized. Please add it to Firebase Console > Authentication > Settings > Authorized domains.`,
        });
      } else if (error.code === 'auth/popup-blocked') {
        toast({
          variant: 'destructive',
          title: 'Popup Blocked',
          description: 'Please allow popups for this site to sign in with Google. Click the icon in your address bar to enable popups.',
        });
      } else if (error.code === 'auth/popup-closed-by-user') {
        const popupClosedHint = mobileUa
          ? 'Google sign-in did not finish in the popup (common on mobile emulation). Retrying with redirect, or use desktop Chrome / emulator quick sign-in.'
          : 'You closed the sign-in window. Please try again when ready.';
        toast({
          variant: 'default',
          title: 'Sign-in Cancelled',
          description: popupClosedHint,
        });
      } else if (error.code === 'auth/cancelled-popup-request') {
        logger.warn('⚠️ [LoginPage] Popup request was cancelled');
        toast({
          variant: 'destructive',
          title: 'Sign-in Cancelled',
          description: 'The sign-in request was cancelled. Please try again.',
        });
      } else if (error.code === 'auth/network-request-failed') {
        logger.error('❌ [LoginPage] Network error during sign-in');
        toast({
          variant: 'destructive',
          title: 'Network Error',
          description: 'Unable to connect. Please check your internet connection and try again.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Sign-in Failed',
          description: error.message || 'An unexpected error occurred during sign-in.',
        });
      }

      // Note: Auth context is preserved for error reporting, will be cleared on success
    }
  }




  const awaitingServerSession = isAuthenticated && !loading && !serverUser;

  // Show loading while auth initializes, email link completes, or server session is created
  if (loading || autoCompletingLink || awaitingServerSession) {
    return (
      <PageLayout
        title="Loading..."
        isLoading={true}
        showNavigation={false}
        compactContent
      >
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground mt-4">
            {awaitingServerSession ? 'Establishing your session…' : 'Loading...'}
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title=""
      showNavigation={false}
      compactContent
    >
      <div className="mx-auto flex w-full max-w-md flex-col px-2 pb-24 pt-2 sm:px-4 md:my-auto md:min-h-[calc(100dvh-4rem)] md:justify-center md:pb-8 md:pt-0">
        <Card className="shadow-2xl">
            <CardHeader>
              <div className="mb-4 flex justify-center">
                <HarryAvatar />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight sm:text-3xl">
                {isPurchaseFlow ? 'Sign in to complete your subscription' : 'Harry - Your Biomechanics Assistant'}
              </CardTitle>
              <CardDescription>
                {isPurchaseFlow
                  ? 'Use Google or a sign-in link to continue to secure checkout.'
                  : 'Sign in to continue to your account.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {loginNotice ? (
                  <Alert data-testid="login-help-notice" variant="destructive">
                    <AlertTitle>{LOGIN_NOTICE_COPY[loginNotice].title}</AlertTitle>
                    <AlertDescription>{LOGIN_NOTICE_COPY[loginNotice].description}</AlertDescription>
                  </Alert>
                ) : null}
                {needsEmailForLink ? (
                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <p className="text-sm text-muted-foreground">
                      Complete sign-in with the link from your email. Enter the address you were
                      invited with.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="link-completion-email">Email</Label>
                      <Input
                        id="link-completion-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        className="scroll-mb-24"
                        value={linkCompletionEmail}
                        onChange={(e) => setLinkCompletionEmail(e.target.value)}
                        onFocus={scrollEmailInputIntoView}
                        placeholder="you@example.com"
                        disabled={completingLink}
                      />
                    </div>
                    <Button
                      className="w-full"
                      disabled={completingLink || !linkCompletionEmail.trim()}
                      onClick={() =>
                        void completeEmailLinkSignIn(
                          linkCompletionEmail,
                          ++emailLinkAttemptRef.current
                        )
                      }
                    >
                      {completingLink ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Completing sign-in…
                        </>
                      ) : (
                        'Complete sign-in'
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                     <Button variant="outline" className="w-full h-12" onClick={() => void signInWithGoogle(false)}>
                      <GoogleIcon className="mr-2 h-5 w-5" />
                      Sign in with Google
                    </Button>
                    {showRedirectFallback && (
                      <div className="mt-2 text-center text-xs">
                        <span className="text-muted-foreground">Sign-in popup blocked or closed? </span>
                        <button
                          type="button"
                          className="text-primary hover:underline font-semibold"
                          onClick={() => void signInWithGoogle(true)}
                        >
                          Try redirect sign-in instead
                        </button>
                      </div>
                    )}
                    {showEmulatorQuickSignIn ? (
                      <div className="space-y-2 rounded-lg border border-dashed border-warning bg-warning-surface/30 p-4">
                        <p className="text-sm font-medium text-warning-text">
                          Emulator quick sign-in
                        </p>
                        <p className="text-xs text-muted-foreground">
                          One-click test users with roles — no Google popup needed.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {EMULATOR_GOOGLE_TEST_USERS.map((user) => (
                            <Button
                              key={user.key}
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={emulatorSigningInAs !== null}
                              onClick={() => void signInAsEmulatorUser(user.key)}
                            >
                              {emulatorSigningInAs === user.key ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Signing in…
                                </>
                              ) : (
                                user.label
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or</span>
                      </div>
                    </div>
                    <div className="space-y-3 rounded-lg border border-border p-4">
                      <p className="text-sm text-muted-foreground">
                        Already invited? Request a new sign-in link to that email (no password). Do not
                        reuse an old link from a previous message.
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="request-link-email">Email</Label>
                        <Input
                          id="request-link-email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          className="scroll-mb-24"
                          value={requestLinkEmail}
                          onChange={(e) => setRequestLinkEmail(e.target.value)}
                          onFocus={scrollEmailInputIntoView}
                          placeholder="you@example.com"
                          disabled={requestingLink}
                        />
                      </div>
                      <Button
                        className="w-full"
                        variant="secondary"
                        disabled={requestingLink || !requestLinkEmail.trim()}
                        onClick={() => void handleRequestSignInLink()}
                      >
                        {requestingLink ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending link…
                          </>
                        ) : (
                          'Email me a sign-in link'
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {!isPurchaseFlow && (
                <div className="mt-6 text-center">
                  <Button variant="link" asChild>
                    <Link to="/welcome">New here? See plans & pricing</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
      </div>
    </PageLayout>
  );
}
